const CHUNK_ERROR_PATTERNS = [
  "Importing a module script failed",
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Loading chunk",
] as const;

const CHUNK_RELOAD_KEY = "chunk-error-reload-at";
const CHUNK_RELOAD_INTERVAL = 15_000;

const isBrowser = typeof window !== "undefined";

export const isChunkLoadError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const reloadForChunkError = () => {
  if (!isBrowser) return false;

  const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0");
  const now = Date.now();

  // Throttle reloads so we never get caught in a refresh loop if the
  // underlying asset is genuinely broken.
  if (now - last <= CHUNK_RELOAD_INTERVAL) return false;

  window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));

  // Cache-bust the next document load so the browser pulls a fresh module
  // graph instead of replaying the stale optimized-deps cache that triggered
  // the import failure in the first place.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", String(now));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
};

export const installChunkErrorRecovery = () => {
  if (!isBrowser) return () => {};

  const handleError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.message || event.error)) {
      reloadForChunkError();
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadForChunkError();
    }
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
};

export const importOnIdle = (loader: () => Promise<unknown>, timeout = 2500) => {
  if (!isBrowser) return;

  const run = () => {
    void loader().catch(() => undefined);
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout });
    return;
  }

  globalThis.setTimeout(run, timeout);
};

export const assertRuntimeState = (checks: Array<() => void>) => {
  for (const check of checks) {
    try {
      check();
    } catch {
      // Keep runtime self-healing and silent in production.
    }
  }
};

export const installHydrationDiagnostics = () => {
  if (!isBrowser) return () => {};

  const originalError = console.error;
  console.error = (...args) => {
    const firstArg = typeof args[0] === "string" ? args[0] : "";
    if (firstArg.includes("Hydration") || firstArg.includes("did not match")) {
      window.sessionStorage.setItem("runtime-hydration-warning", String(Date.now()));
    }
    originalError(...args);
  };

  return () => {
    console.error = originalError;
  };
};
